import {
  colorGray,
  colorLink,
  withOpacity,
} from '@atlas-frontend/design-tokens';
import { Header } from '@tanstack/react-table';
import {
  FC,
  PointerEventHandler,
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import styled from 'styled-components';
import { MIN_COLUMN_WIDTH } from '../Table';
import { useTableStoreValue } from '../TableContext';
import { useInitializeColumnSizing } from './useInitializeColumnSizing';

export interface ColumnResizeHandleProps extends PropsWithChildren {
  header: Header<any, unknown>;
}

export const ColumnResizeHandle: FC<ColumnResizeHandleProps> = (props) => {
  const { header } = props;
  const isResizing = header.column.getIsResizing();
  const resizerRef = useRef<HTMLDivElement>(null);
  const handleResize = header.getResizeHandler();
  const initColumnSizing = useInitializeColumnSizing();

  const handleResizePointerDown: PointerEventHandler<HTMLDivElement> =
    useCallback(
      (event) => {
        event.stopPropagation();
        initColumnSizing();
        setTimeout(() => {
          handleResize(event);
        });
      },
      [handleResize, initColumnSizing]
    );

  const { columnSizingInfo } = header.getContext().table.getState();
  const width = header.getSize();
  const offset = Math.max(
    columnSizingInfo.deltaOffset ?? 0,
    MIN_COLUMN_WIDTH - width
  );

  const [wrapperRef] = useTableStoreValue('wrapperRef');

  const tableContentHeight = useMemo(() => {
    if (!isResizing || !wrapperRef.current || !resizerRef.current) {
      return 0;
    }
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const resizerRect = resizerRef.current.getBoundingClientRect();
    return wrapperRect.bottom - resizerRect.bottom;
  }, [isResizing, wrapperRef]);

  return (
    <>
      {isResizing && (
        <>
          <HighlightedLeftBorder $tableContentHeight={tableContentHeight} />
          <HighlightedStretch
            $tableContentHeight={tableContentHeight}
            style={{ width: `${width + offset}px` }}
          />
        </>
      )}
      <StyledResizeHandle
        ref={resizerRef}
        $tableContentHeight={tableContentHeight}
        className={`resizer ${isResizing ? 'isResizing' : ''}`}
        onPointerDown={handleResizePointerDown}
        style={{ transform: isResizing ? `translateX(${offset}px)` : '' }}
      />
    </>
  );
};

const HighlightedLeftBorder = styled.div<{ $tableContentHeight: number }>`
  position: absolute;
  z-index: 2;
  top: 0;
  left: -1px;
  width: 2px;
  height: 100%;
  background-color: ${colorLink};

  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 100%;
    width: 100%;
    height: ${(p) => p.$tableContentHeight}px;
    background: ${colorLink};
  }
`;

const HighlightedStretch = styled.div<{ $tableContentHeight: number }>`
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${withOpacity(colorLink, 0.1)};
  border: 0;
  border-top: 2px solid ${colorLink};

  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 100%;
    width: 100%;
    height: ${(p) => p.$tableContentHeight}px;
    background: ${withOpacity(colorLink, 0.1)};
    border: 0;
    border-bottom: 2px solid ${colorLink};
  }
`;

const StyledResizeHandle = styled.div<{ $tableContentHeight: number }>`
  position: absolute;
  top: 0;
  right: -2px;
  width: 5px;
  height: 100%;
  cursor: col-resize;

  &:before {
    content: '';
    position: absolute;
    left: 2px;
    width: 1px;
    height: 100%;
    background-color: ${colorGray[3]};
    transition: background-color 0.2s;
  }

  &:after {
    content: '';
    position: absolute;
    left: 2px;
    top: 100%;
    width: 2px;
    height: ${(p) => p.$tableContentHeight}px;
    background: ${colorLink};
    display: none;
  }

  &:hover,
  &.isResizing {
    z-index: 2;

    &:before {
      width: 2px;
      background-color: ${colorLink};
    }
  }

  &.isResizing:after {
    display: block;
  }
`;
